import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';

// Lightweight canvas waveform player. No external deps.
// - Decodes audio once, samples peaks
// - Click waveform to seek
// - Play/pause button + time display

const BAR_WIDTH = 3;
const BAR_GAP = 1;
const BAR_MIN_HEIGHT = 2;

export const WaveformPlayer = ({ src, height = 56, previewSeconds = 0, testIdPrefix = 'waveform' }) => {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const peaksRef = useRef([]);
  const rafRef = useRef(null);

  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  // Load & decode audio → peaks
  useEffect(() => {
    let cancelled = false;
    if (!src) { setState('idle'); return; }
    setState('loading');

    const load = async () => {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error('fetch failed');
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const decoded = await ctx.decodeAudioData(buf.slice(0));
        if (cancelled) return;

        const canvas = canvasRef.current;
        const cssWidth = canvas ? canvas.clientWidth : 600;
        const barCount = Math.max(40, Math.floor(cssWidth / (BAR_WIDTH + BAR_GAP)));
        const channelData = decoded.getChannelData(0);
        const blockSize = Math.max(1, Math.floor(channelData.length / barCount));
        const peaks = new Array(barCount);
        for (let i = 0; i < barCount; i++) {
          let sum = 0;
          const start = i * blockSize;
          const end = Math.min(start + blockSize, channelData.length);
          for (let j = start; j < end; j++) sum += Math.abs(channelData[j]);
          peaks[i] = sum / (end - start || 1);
        }
        // Normalize
        const max = Math.max(...peaks, 0.001);
        peaksRef.current = peaks.map((p) => p / max);
        setDuration(decoded.duration);
        setState('ready');
        try { await ctx.close(); } catch (e) { /* noop */ }
      } catch (e) {
        if (!cancelled) setState('error');
      }
    };

    load();
    return () => { cancelled = true; };
  }, [src]);

  // Draw canvas whenever peaks or progress changes
  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const peaks = peaksRef.current;
    if (!peaks.length) return;

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = height;
    if (canvas.width !== cssWidth * dpr || canvas.height !== cssHeight * dpr) {
      canvas.width = cssWidth * dpr;
      canvas.height = cssHeight * dpr;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const total = peaks.length;
    const barCount = total;
    const totalWidth = barCount * (BAR_WIDTH + BAR_GAP);
    const startX = Math.max(0, (cssWidth - totalWidth) / 2);
    const progressRatio = duration > 0 ? current / duration : 0;
    const progressX = startX + progressRatio * totalWidth;

    for (let i = 0; i < barCount; i++) {
      const x = startX + i * (BAR_WIDTH + BAR_GAP);
      const barHeight = Math.max(BAR_MIN_HEIGHT, peaks[i] * (cssHeight - 4));
      const y = (cssHeight - barHeight) / 2;
      const isPlayed = x <= progressX;
      // Gradient bar - purple → cyan for played, muted white for unplayed
      const grd = isPlayed
        ? (() => {
            const g = ctx.createLinearGradient(x, y, x, y + barHeight);
            g.addColorStop(0, '#a78bfa');
            g.addColorStop(1, '#22d3ee');
            return g;
          })()
        : 'rgba(255,255,255,0.18)';
      ctx.fillStyle = grd;
      ctx.fillRect(x, y, BAR_WIDTH, barHeight);
    }
  };

  useEffect(() => { draw(); /* eslint-disable-next-line */ }, [state, current, duration, height]);

  // Redraw on resize
  useEffect(() => {
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line
  }, [state, current, duration]);

  // rAF for smooth progress
  useEffect(() => {
    const tick = () => {
      const el = audioRef.current;
      if (el) setCurrent(el.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    if (playing) rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [playing]);

  // Enforce preview cap
  useEffect(() => {
    if (!previewSeconds) return;
    if (current >= previewSeconds && playing) {
      const el = audioRef.current;
      if (el) { el.pause(); el.currentTime = 0; }
      setPlaying(false);
    }
  }, [current, previewSeconds, playing]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const handleSeek = (e) => {
    if (state !== 'ready') return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const newTime = ratio * duration;
    const el = audioRef.current;
    if (el) el.currentTime = newTime;
    setCurrent(newTime);
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  if (!src) return null;

  return (
    <div className="w-full max-w-md" data-testid={`${testIdPrefix}-container`}>
      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          disabled={state !== 'ready'}
          className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`${testIdPrefix}-play-button`}
          type="button"
        >
          {state === 'loading' ? (
            <Loader2 className="w-4 h-4 text-white animate-spin" />
          ) : playing ? (
            <Pause className="w-4 h-4 text-white" fill="currentColor" />
          ) : (
            <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <canvas
            ref={canvasRef}
            onClick={handleSeek}
            style={{ width: '100%', height: `${height}px`, cursor: state === 'ready' ? 'pointer' : 'default' }}
            data-testid={`${testIdPrefix}-canvas`}
          />
        </div>

        <span className="text-xs text-gray-400 font-mono flex-shrink-0 tabular-nums w-16 text-right">
          {state === 'error' ? '—' : `${fmt(current)}/${fmt(duration)}`}
        </span>
      </div>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onEnded={() => { setPlaying(false); setCurrent(0); }}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
      />
    </div>
  );
};
